// What a workspace looks like on the "Choose a workspace" page: two initials
// on a tinted square, and its role in plain words.

/**
 * Up to two initials for a workspace or customer name.
 *
 * "Katoen Natie Singapore" is KN, "Calvin-WYN2000 test" is CW, a single word
 * gives its first two letters. Words are split on anything that is not a
 * letter or a digit, so a hyphenated name counts as two.
 */
export function workspaceInitials(name) {
  const words = String(name ?? '').split(/[^\p{L}\p{N}\p{M}]+/u).filter(Boolean);
  if (words.length === 0) return '?';
  const letters = words.length === 1
    ? initialLetters(words[0]).slice(0, 2)
    : [initialLetters(words[0])[0], initialLetters(words[1])[0]];
  return letters.join('').toUpperCase();
}

// Thai and Lao write some vowels before the consonant they follow in speech
// (เ แ โ ใ ไ), and tone and vowel marks combine with the letter before them.
// Neither is what a reader takes as a word's initial: "ใจดี" starts with จ,
// and an avatar reading "สใ" for "สมชาย ใจดี" looked broken.
const LEADING_VOWEL = /[\u0E40-\u0E44\u0EC0-\u0EC4]/u;

/** The letters of a word that can stand as its initial, in order. */
export function initialLetters(word) {
  const all = [...String(word ?? '')];
  const letters = all.filter((ch) => /[\p{L}\p{N}]/u.test(ch) && !LEADING_VOWEL.test(ch));
  return letters.length ? letters : all.filter((ch) => !/\p{M}/u.test(ch));
}

// Soft grounds with a text colour that reads on them. Five is enough to tell
// neighbours apart without turning the page into a paint chart.
export const WORKSPACE_TINTS = [
  { bg: '#e9f4ec', fg: '#137a3e' },
  { bg: '#e8f0fb', fg: '#1c4f9c' },
  { bg: '#fdf2dd', fg: '#8a5a00' },
  { bg: '#f1eafa', fg: '#5b2d8e' },
  { bg: '#e4f4f5', fg: '#0f6470' },
];

/**
 * The tint of the card at `index` in its list: the set in turn, so two cards
 * side by side never share one. Picking it from the name gave three amber
 * cards out of six.
 */
export function workspaceTint(index) {
  const i = Number.isInteger(index) && index >= 0 ? index : 0;
  return WORKSPACE_TINTS[i % WORKSPACE_TINTS.length];
}

/** "admin" as "Admin"; an unknown or missing role as it is, or as nothing. */
export function roleLabel(role) {
  const text = String(role ?? '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}
